import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { BASE_PATH } from '@smatchy/sdk';

export interface OrganizerBalancesExportFilters {
  search?: string;
  stripeStatus?: 'connected' | 'not-started' | 'access-revoked' | null;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Downloads the organizer balances list as a CSV the API builds.
 *
 * The generated SDK has no operation that returns a Blob, so this calls the
 * endpoint by hand. The URL is the sibling of the SDK's own list call
 * (`${basePath}/admin/organizers/balances`, no extra prefix), so the export
 * lands on the same host and prefix as the list already shown on the page.
 * Going through HttpClient (not a plain navigation) lets the TokenInterceptor
 * add the bearer the admin route needs.
 *
 * The same search and stripeStatus filters as the list are sent, so the file is
 * the filtered view and not the whole table. page/limit are never sent: the API
 * drives its own pagination to the cap.
 */
@Injectable({ providedIn: 'root' })
export class OrganizerBalancesExportApiService {
  private readonly basePath: string;

  constructor(private http: HttpClient, @Optional() @Inject(BASE_PATH) basePath?: string) {
    this.basePath = (basePath || 'http://localhost:3000').replace(/\/$/, '');
  }

  exportOrganizerBalancesCsv(filters: OrganizerBalancesExportFilters = {}): Observable<Blob> {
    let params = new HttpParams();
    const search = filters.search?.trim();
    if (search) {
      params = params.set('search', search);
    }
    if (filters.stripeStatus) {
      params = params.set('stripeStatus', filters.stripeStatus);
    }
    if (filters.sortBy) {
      params = params.set('sortBy', filters.sortBy);
    }
    if (filters.sortOrder) {
      params = params.set('sortOrder', filters.sortOrder);
    }

    return this.http.get(`${this.basePath}/admin/organizers/balances/export/csv`, {
      params,
      responseType: 'blob',
    });
  }
}
